import axios from 'axios';
import useAuthStore from '../store/authStore';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().clearUser();
    }
    return Promise.reject(error);
  }
);

// Get ebooks with chapters (paginated)
export const getEbooksWithChapters = async (page = 1, limit = 10) => {
  const response = await api.get('/ebooks/with-chapters', {
    params: { page, limit }
  });
  return response.data;
};

// Get single ebooks (paginated)
export const getSingleEbooks = async (page = 1, limit = 10) => {
  const response = await api.get('/ebooks/single', {
    params: { page, limit }
  });
  return response.data;
};

// Get trending ebooks (paginated)
export const getTrendingEbooks = async (page = 1, limit = 10) => {
  const response = await api.get('/ebooks/trending', {
    params: { page, limit }
  });
  return response.data;
};

// Search ebooks (paginated)
export const searchEbooks = async (query, page = 1, limit = 10) => {
  const response = await api.get('/ebooks/search', {
    params: { query, page, limit }
  });
  return response.data;
};

// Get ebook by ID
export const getEbookById = async (id) => {
  const response = await api.get(`/ebooks/id/${id}`);
  return response.data;
};

// Get ebook by slug
export const getEbookBySlug = async (slug) => {
  const response = await api.get(`/ebooks/slug/${slug}`);
  return response.data;
};

// Get ebooks by category (paginated)
export const getEbooksByCategory = async (categoryId, page = 1, limit = 10) => {
  const response = await api.get(`/ebooks/category/${categoryId}`, {
    params: { page, limit }
  });
  return response.data;
};

// Get ebooks by author (paginated)
export const getEbooksByAuthor = async (author, page = 1, limit = 10) => {
  const response = await api.get(`/ebooks/author/${author}`, {
    params: { page, limit }
  });
  return response.data;
};

// Toggle like for an ebook (requires authentication)
export const toggleEbookLike = async (id) => {
  const response = await api.post(`/ebooks/${id}/toggle-like`);
  return response.data;
};


export const getSimilarEbooks = async (id, limit = 5) => {
    const response = await api.get(`/ebooks/${id}/similar`, {
      params: { limit }
    });
    return response.data;
  };

export const getEbooksByLanguage = async (language = 'english', page = 1, limit = 10) => {
  const response = await api.get(`/ebooks/by-language`, {
    params: { language, page, limit },
  });
  return response.data;
};

export const findEbooksByGenreName = async (genreName, page = 1, limit = 10, sortBy = 'created_at', sortOrder = 'DESC') => {
  try {
    const response = await api.get(`/ebooks/genre/${encodeURIComponent(genreName)}`, {
      params: { page, limit, sortBy, sortOrder }
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('No ebooks found for this genre');
    }
    throw error;
  }
};

export const findEbooksByGenreSlug = async (genreSlug, page = 1, limit = 10, sortBy = 'created_at', sortOrder = 'DESC') => {
  try {
    const response = await api.get(`/ebooks/genre-slug/${encodeURIComponent(genreSlug)}`, {
      params: { page, limit, sortBy, sortOrder }
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('No ebooks found for this genre slug');
    }
    throw error;
  }
};

export const useCategoryMetaBySlug = (type, slug, language) => {
  const { setError } = useAuthStore();

  return useQuery(
    ['category-meta', type, slug, language],
    async () => {
      const res = await api.get(`/categories/${type}/slug/${slug}`, {
        params: language ? { language } : {},
      });
      return res.data;
    },
    {
      enabled: !!type && !!slug,
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      staleTime: 300000,
      cacheTime: 3600000,
    }
  );
};

